import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SessionKit } from '@wharfkit/session';
import { WebRenderer } from '@wharfkit/web-renderer';
import { WalletPluginAnchor } from '@wharfkit/wallet-plugin-anchor';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Tag } from 'primereact/tag';
import configuration from './configuration.json';

// GPU admin page: shows what holds memory on the gpuvm V100 and frees it.
// Talks to gpu-admin (gpu-admin/gpu_admin.py on gpuvm):
//   prod  same origin /gpu-admin  (nginx on .227 -> gpuvm-flux-tunnel)
//   dev   http://127.0.0.1:8520   (the Mac's gpuvm SSH tunnel)
// Sign-in reuses the labelling API's bcauth passlogin flow. gpu-admin checks
// the token itself and only accepts accounts on its admin list, so hiding the
// menu item from non-admins is cosmetic.
const IS_LOCAL_HOST = typeof window !== 'undefined'
    && ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
const ADMIN_API = IS_LOCAL_HOST ? 'http://127.0.0.1:8520' : '/gpu-admin';
// The token must come from the deployed labelling API, whose secret gpu-admin
// shares (a local dev labelling API signs with a different one).
const LOGIN_API = IS_LOCAL_HOST ? 'https://passer.scpdx.net/api' : '/api';
const TOKEN_KEY = 'gpu_admin_jwt';
const REFRESH_MS = 5000;

const gb = (mib) => (mib / 1024).toFixed(1);
const MODEL_SEVERITY = { loaded: 'warning', loading: 'info', sleeping: 'success' };

const AdminPanel = () => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [running, setRunning] = useState(null);   // label of the action in flight
    const [signingIn, setSigningIn] = useState(false);
    // LoRA picked in each server's dropdown; absent = show what the server has.
    const [loraChoice, setLoraChoice] = useState({});

    const sessionKitRef = useRef(null);
    if (!sessionKitRef.current) {
        sessionKitRef.current = new SessionKit({
            appName: 'passer-admin',
            chains: [{
                id:  configuration.passer.BCid,
                url: localStorage.getItem('bcEndpoint') || configuration.passer.BCEndPoints[0].url,
            }],
            ui: new WebRenderer(),
            walletPlugins: [new WalletPluginAnchor()],
        });
    }

    const signOut = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setStatus(null);
    }, []);

    const api = useCallback(async (path, init = {}) => {
        const r = await fetch(ADMIN_API + path, {
            ...init,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        });
        const body = await r.json().catch(() => ({}));
        if (r.status === 401) signOut();
        if (!r.ok) {
            throw new Error(body.detail || (r.status === 502
                ? 'gpu-admin is not reachable (service down or tunnel missing)'
                : `HTTP ${r.status}`));
        }
        return body;
    }, [token, signOut]);

    const refresh = useCallback(async () => {
        try {
            setStatus(await api('/status'));
            setError(null);
        } catch (e) {
            setError(e.message);
        }
    }, [api]);

    useEffect(() => {
        if (!token) return undefined;
        refresh();
        const id = setInterval(() => { if (!document.hidden) refresh(); }, REFRESH_MS);
        return () => clearInterval(id);
    }, [token, refresh]);

    // Same flow as LabellingTests: sign passlogin without broadcasting, the
    // labelling API broadcasts it (chain checks signature + passallowed) and
    // returns a token.
    const signIn = async () => {
        setSigningIn(true);
        setError(null);
        let session = null;
        try {
            session = (await sessionKitRef.current.login()).session;
            const result = await session.transact({
                actions: [{
                    account: 'bcauth',
                    name: 'passlogin',
                    authorization: [{
                        actor: session.permissionLevel.actor,
                        permission: session.permissionLevel.permission,
                    }],
                    data: { user: session.permissionLevel.actor },
                }],
            }, { broadcast: false });
            const packedTrx = Array.from(result.resolved.serializedTransaction)
                .map(b => b.toString(16).padStart(2, '0')).join('');
            const r = await fetch(`${LOGIN_API}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    account: String(session.permissionLevel.actor),
                    packed_trx: packedTrx,
                    signatures: result.signatures.map(s => String(s)),
                }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.error || `Sign-in failed (HTTP ${r.status})`);
            localStorage.setItem(TOKEN_KEY, j.access_token);
            setToken(j.access_token);
        } catch (e) {
            console.error(e);
            setError(e.message || String(e));
        } finally {
            if (session) {
                try { await sessionKitRef.current.logout(session); } catch (_) {}
            }
            setSigningIn(false);
        }
    };

    const run = async (label, path, body, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setRunning(label);
        setNotice(null);
        try {
            const j = await api(path, { method: 'POST', body: JSON.stringify(body || {}) });
            setNotice({ severity: 'success', text: j.message });
        } catch (e) {
            setNotice({ severity: 'error', text: e.message });
        } finally {
            setRunning(null);
            refresh();
        }
    };

    const actionButton = (label, text, onClick, extra = {}) => (
        <Button label={text} size="small" loading={running === label}
                disabled={!!running || extra.disabled} onClick={onClick}
                className={extra.className || 'p-button-outlined'}
                style={{ marginRight: 8, marginTop: 8 }} />
    );

    if (!token) {
        return (
            <div style={{ maxWidth: 640, margin: '2rem auto' }}>
                <Card title="GPU Admin">
                    <p>Sign in with Anchor to manage GPU memory on gpuvm (Tesla V100).
                       Only GPU admin accounts can use this page.</p>
                    {error && <Message severity="error" text={error} style={{ marginBottom: 12 }} />}
                    <Button label="Sign in with Anchor" icon="pi pi-key" loading={signingIn} onClick={signIn} />
                </Card>
            </div>
        );
    }

    const gpu = status && status.gpu;
    const flux = status && status.flux;
    const llama = status && status.llama;
    const ollama = status && status.ollama;
    const usedPct = gpu && !gpu.error ? Math.round(100 * gpu.memory_used_mib / gpu.memory_total_mib) : 0;
    const ram = status && status.ram;
    const ramPct = ram ? Math.round(100 * ram.used_mib / ram.total_mib) : 0;
    const barColor = (pct) => (pct > 85 ? '#dc2626' : pct > 50 ? '#d97706' : '#16a34a');

    const loadedLlama = llama ? llama.models.filter(m => m.status !== 'unloaded') : [];

    // One card per FLUX server (flux2 = FLUX.2-dev :8510, flux1 = FLUX.1-dev :8511)
    const renderFlux = (name, f) => {
        const state = !f.on ? 'switched off' : f.error ? 'not responding'
            : f.loading ? 'loading model…' : f.busy ? 'generating'
            : f.model_loaded ? 'model loaded' : 'model unloaded';
        // Loading (minutes) and generating are normal work, not a hang:
        // restarting then throws the work away, so it needs a forced confirm.
        const working = f.loading || f.busy;
        const power = (on) => run(`${name}-power`, `/flux/${name}/power${!on && (f.error || f.loading) ? '?force=true' : ''}`, { on },
            on ? `Switch ${f.title} on? It starts now and at every boot.`
               : `Switch ${f.title} off? It stops now (freeing all its memory) and stays off after reboots `
                 + `until you switch it on. Its option on the image pages won't work meanwhile.`);
        const lora = f.lora;
        const loraValue = name in loraChoice ? loraChoice[name] : lora.selected || null;
        const loraOptions = [
            { label: 'No LoRA', value: null },
            ...lora.available.map(l => ({
                label: l.fits ? l.file : `${l.file} (doesn't fit)`,
                value: l.file,
                disabled: !l.fits,
            })),
        ];
        const clearChoice = () => setLoraChoice(c => { const { [name]: _, ...rest } = c; return rest; });
        return (
            <Card key={name} style={{ marginTop: 16 }} title={`${f.title} image API`}
                  subTitle={`${f.service_name}.service · port ${f.port} · ${f.service}`
                            + (f.enabled_at_boot ? ' · starts at boot' : ' · off at boot')}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <InputSwitch checked={f.on} onChange={(e) => power(e.value)}
                                 disabled={!!running || (f.on && f.busy)} />
                    <span>{running === `${name}-power` ? (f.on ? 'Switching off…' : 'Switching on…') : f.on ? 'On' : 'Off'}</span>
                    <Tag value={state}
                         severity={!f.on ? null : f.error ? 'danger' : working ? 'info' : f.model_loaded ? 'warning' : 'success'} />
                    {f.on && f.ram_mib > 0 && <span>RAM {gb(f.ram_mib)} GB</span>}
                </div>
                {f.error && <p style={{ marginBottom: 0 }}>{f.error}</p>}
                {f.on && <div>
                    {actionButton(`${name}-unload`, 'Unload model',
                        () => run(`${name}-unload`, `/flux/${name}/unload`),
                        { disabled: !f.model_loaded || working || !!f.error })}
                    {actionButton(`${name}-restart`, 'Restart service',
                        () => run(`${name}-restart`, `/flux/${name}/restart${f.error || f.loading ? '?force=true' : ''}`, null,
                            f.loading
                                ? `${f.title} is loading its model for a waiting image (this takes a few minutes). Restarting cancels that request. Restart anyway?`
                                : f.error
                                ? `${f.title} is not responding. Restart it?`
                                : `Restart ${f.title}? This frees all its GPU memory; the next image waits for the model to load.`),
                        { disabled: f.busy, className: 'p-button-outlined p-button-warning' })}
                </div>}
                <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 6 }}>
                        <b>LoRA:</b> {lora.selected || 'none'}
                        {lora.selected && (
                            <Tag style={{ marginLeft: 8 }}
                                 value={lora.loaded ? 'applied' : f.on ? 'applies when the model loads' : 'applies when switched on'}
                                 severity={lora.loaded ? 'success' : 'info'} />
                        )}
                    </div>
                    {lora.available.length === 0 ? (
                        <small>No LoRAs yet. Put {f.title} LoRA files (.safetensors) in <code>{lora.dir}</code> on gpuvm.</small>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Dropdown value={loraValue} options={loraOptions}
                                      onChange={(e) => setLoraChoice(c => ({ ...c, [name]: e.value }))}
                                      disabled={!!running} style={{ minWidth: 280, marginRight: 8, marginTop: 8 }} />
                            {actionButton(`${name}-lora`, f.on ? `Apply (restarts ${f.title})` : 'Save for next start',
                                () => run(`${name}-lora`, `/flux/${name}/lora`, { file: loraValue },
                                    f.on ? `Restart ${f.title} with ${loraValue || 'no LoRA'}? The next image waits for the model to load.` : null)
                                    .then(clearChoice),
                                { disabled: f.busy || loraValue === (lora.selected || null) })}
                        </div>
                    )}
                </div>
            </Card>
        );
    };

    return (
        <div style={{ maxWidth: 960, margin: '1.5rem auto', padding: '0 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0 }}>GPU Admin · gpuvm</h2>
                <div>
                    {status && <span style={{ marginRight: 12 }}>Signed in as <b>{status.account}</b></span>}
                    <Button label="Sign out" size="small" className="p-button-text" onClick={signOut} />
                </div>
            </div>

            {error && <Message severity="error" text={error} style={{ marginTop: 12, width: '100%' }} />}
            {notice && <Message severity={notice.severity} text={notice.text} style={{ marginTop: 12, width: '100%' }} />}

            {gpu && (
                <Card style={{ marginTop: 16 }}
                      title={gpu.error ? 'GPU' : `${gpu.name}`}
                      subTitle={gpu.error ? gpu.error
                          : `${gb(gpu.memory_used_mib)} of ${gb(gpu.memory_total_mib)} GB used · `
                            + `${gpu.utilization_pct}% busy · ${gpu.temperature_c} °C`}>
                    <ProgressBar value={usedPct} color={barColor(usedPct)} />
                    {ram && (
                        <div style={{ marginTop: 14 }}>
                            <div style={{ marginBottom: 4 }}>
                                <b>RAM</b> (s7b host, shared by all containers): {gb(ram.used_mib)} of {gb(ram.total_mib)} GB used
                            </div>
                            <ProgressBar value={ramPct} color={barColor(ramPct)} />
                            <small>FLUX.1 loads into RAM rather than the GPU (CPU offload), so its loading shows here and on its card.</small>
                        </div>
                    )}
                </Card>
            )}

            {flux && Object.entries(flux).map(([name, f]) => renderFlux(name, f))}

            {llama && (
                <Card style={{ marginTop: 16 }} title="Muse Glimmer / Qwen (llama.cpp)"
                      subTitle={`llama-muse.service · ${llama.service} · one model at a time, sleeps after 5 min idle`}>
                    {llama.error && <Message severity="warn" text={llama.error} />}
                    {llama.models.map(m => (
                        <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                            <Tag value={m.status} severity={MODEL_SEVERITY[m.status]} style={{ minWidth: 80 }} />
                            <span style={{ flex: 1 }}>{m.id}</span>
                            {m.status !== 'unloaded' && actionButton(`llama-${m.id}`, 'Unload',
                                () => run(`llama-${m.id}`, '/llama/unload', { model: m.id }))}
                        </div>
                    ))}
                    {loadedLlama.length > 1 && actionButton('llama-all', 'Unload all',
                        () => run('llama-all', '/llama/unload', {}))}
                </Card>
            )}

            {ollama && (
                <Card style={{ marginTop: 16 }} title="Ollama" subTitle={`ollama.service · ${ollama.service}`}>
                    {ollama.error && <Message severity="warn" text={ollama.error} />}
                    {!ollama.error && ollama.models.length === 0 && <span>No models loaded.</span>}
                    {ollama.models.map(m => (
                        <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                            <Tag value={`${gb(m.size_vram_mib)} GB`} severity="warning" style={{ minWidth: 80 }} />
                            <span style={{ flex: 1 }}>{m.name}</span>
                            {actionButton(`ollama-${m.name}`, 'Unload',
                                () => run(`ollama-${m.name}`, '/ollama/unload', { model: m.name }))}
                        </div>
                    ))}
                    {ollama.models.length > 1 && actionButton('ollama-all', 'Unload all',
                        () => run('ollama-all', '/ollama/unload', {}))}
                </Card>
            )}
        </div>
    );
};

export default AdminPanel;
